import { useMemo } from 'react';
import { Locations } from '../types/Locations';
import { useAvailableLocations } from './useAvailableLocations';

interface AirportDataAccessResult {
  locations: Locations[];
  loading: boolean;
}

/**
 * Returns airport location IDs that the current user has access to.
 *
 * This hook fetches the user's available locations from the backend
 * (cached for 5 minutes) and returns only the location IDs they can access.
 *
 * This ensures we only request forecast data for locations the user
 * actually has permission to view, avoiding unnecessary API calls and
 * preventing "No forecast data available" errors.
 *
 * @returns Object with accessible location IDs and loading state
 */
export function useAirportDataAccess(): AirportDataAccessResult {
  const { availableLocations, loading } = useAvailableLocations();

  const locations = useMemo(() => {
    if (loading) return [];
    return availableLocations.map(loc => loc.id as Locations);
  }, [availableLocations, loading]);

  return { locations, loading };
}
